import { getActionElement, getElementOfType } from "../utils/dom";
import { ReactiveElement, ReactiveState } from "@nrk/reactive-element";
import platform from "../utils/platform";
import { PlayerActions } from "../player/player-actions";
import { throttle } from "throttle-debounce";
import { AutofocusController } from "@nrk/element-autofocus-controller";
class ControlsElement extends ReactiveElement {
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      active: Boolean
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      fullscreenState: Object,
      isCurrentTimeLive: Boolean,
      isEnded: Boolean,
      isLive: Boolean,
      isPlaying: Boolean,
      remotePlaybackState: Object,
      subtitleState: ReactiveState,
      interactionPointState: Object,
      pipState: Object,
      isStarted: Boolean,
      muted: Boolean
    };
  }
  active = false;
  fullscreenState;
  isCurrentTimeLive = false;
  isEnded = false;
  isLive = false;
  isPlaying = false;
  isStarted = false;
  interactionPointState;
  remotePlaybackState;
  subtitleState;
  pipState;
  muted = false;
  action = PlayerActions.TogglePlay;
  hidden = true;
  activatedAt;
  isHovering = true;
  isPointerOutside = false;
  isPressing = false;
  player;
  deactivateTimeout = 2e3;
  _deactivateTimeoutId;
  _throttledHandleEvent = throttle(60, this.handleEvent);
  _unshadowProperties;
  constructor() {
    super();
    this.deactivate = this.deactivate.bind(this);
    new AutofocusController(this, {
      shouldAutofocus: () => {
        return this.hasUpdated("isConnected") || this.hasUpdated("hidden") || this.active && this.hasUpdated("active");
      }
    });
  }
  readyCallback() {
    this.tabIndex = 0;
    this.player = getElementOfType(this);
    this.addEventListener("click", this);
    this.addEventListener("focus", this, true);
    this.addEventListener("blur", this, true);
    this.addEventListener(
      "touchstart",
      this.handleTouchStartHoverProxyEvent,
      platform.supportsPassiveEvents ? { passive: true } : void 0
    );
    this.addEventListener(platform.pointerEventTypes.enter, this);
    this.addEventListener(platform.pointerEventTypes.down, this);
    this._unshadowProperties = this.player.shadowProperties(
      [
        "fullscreenState",
        "pipState",
        "isCurrentTimeLive",
        "isEnded",
        "isLive",
        "isPlaying",
        "isStarted",
        "interactionPointState",
        "muted",
        "subtitleState"
      ],
      this
    );
  }
  disconnectedCallback() {
    this.removeEventListener("click", this);
    this.removeEventListener("focus", this, true);
    this.removeEventListener("blur", this, true);
    this.removeEventListener("touchstart", this.handleTouchStartHoverProxyEvent);
    this.removeEventListener(platform.pointerEventTypes.enter, this);
    this.removeEventListener(platform.pointerEventTypes.down, this);
    this.removeEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
    this.removeEventListener(platform.pointerEventTypes.leave, this);
    document.removeEventListener(platform.pointerEventTypes.up, this);
    this._unshadowProperties?.();
    this.player = null;
    super.disconnectedCallback();
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "isStarted" && this.isStarted) {
      this.hidden = false;
    }
  }
  handleEvent(event) {
    const { target, type } = event;
    if (type === "focus") {
      this.activate(true, type);
    } else if (type === "blur") {
      const relatedTarget = event.relatedTarget;
      if (this.isPlaying && (!relatedTarget || !this.contains(relatedTarget))) {
        this.activate(false, type);
      }
    } else if (type === "click") {
      if (getActionElement(target) !== this) {
        this.activate(true, "click nested actionElement");
      } else if (!this.isHovering) {
        const msSinceLastActivation = new Date().getTime() - (this.activatedAt || 0);
        if (msSinceLastActivation > 100) {
          this.activate(!this.active, type);
        }
        event.preventDefault();
      }
      this.isHovering = true;
    } else {
      this.handlePointerEvent(event);
    }
  }
  handleTouchStartHoverProxyEvent() {
    this.isHovering = false;
  }
  handlePointerEvent(event) {
    const { target, type } = event;
    if (type === platform.pointerEventTypes.enter) {
      this.isPointerOutside = false;
      this.isPressing = false;
      this.addEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
      this.addEventListener(platform.pointerEventTypes.leave, this);
    } else if (type === platform.pointerEventTypes.move) {
      if (!this.isPointerOutside) {
        this.activate(true, type);
      }
    } else if (type === platform.pointerEventTypes.down) {
      this.isPressing = true;
      this.removeEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
      this.removeEventListener(platform.pointerEventTypes.leave, this);
      document.addEventListener(platform.pointerEventTypes.up, this);
    } else if (type === platform.pointerEventTypes.up) {
      this.isPressing = false;
      const isReleaseOutside = !this.contains(target);
      if (isReleaseOutside) {
        this.isHovering = true;
      } else if (this.isHovering) {
        this.addEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
        this.addEventListener(platform.pointerEventTypes.leave, this);
      }
      if (this.isHovering || this.player.isSeeking) {
        this.activate(!isReleaseOutside, type);
      }
      document.removeEventListener(platform.pointerEventTypes.up, this);
    } else if (type === platform.pointerEventTypes.leave) {
      this.isPointerOutside = true;
      this.removeEventListener(platform.pointerEventTypes.move, this._throttledHandleEvent);
      this.removeEventListener(platform.pointerEventTypes.leave, this);
      this.activate(!this.isPlaying || this.player.focusVisible, type);
    }
  }
  activate(value, reason) {
    window.clearTimeout(this._deactivateTimeoutId);
    this.active = value;
    if (this.active) {
      this._deactivateTimeoutId = window.setTimeout(this.deactivate, this.deactivateTimeout);
      this.activatedAt = new Date().getTime();
    }
  }
  deactivate() {
    const shouldDeactivate = this.isPlaying && !this.isPressing && !this.player.focusVisible;
    if (shouldDeactivate) {
      this.active = false;
    }
  }
}
export {
  ControlsElement
};
